import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../Loader';
import useDebounce from '../../../utils/useDebounce';

// Note: other admin toolbars import `@material-ui/pickers` (KeyboardDatePicker) but never
// actually render it, and the app never wraps its tree in a MuiPickersUtilsProvider — using
// it here would throw at runtime. Native date inputs are used instead, styled to match `.drop`.

const TYPE_OPTIONS = [
  { value: '', label: 'Type · Any' },
  { value: 'prize', label: 'Type · Prize' },
  { value: 'achievement', label: 'Type · Achievement' },
];

const CLAIMED_OPTIONS = [
  { value: '', label: 'Claimed · Any' },
  { value: 'claimed', label: 'Claimed' },
  { value: 'unclaimed', label: 'Unclaimed' },
];

const LoyaltyToolbar = ({
  filters, onChange, slugOptions, onExport, downloadingInProgress,
}) => {
  const [emailSearch, setEmailSearch] = useState(filters.email || '');
  const debouncedEmail = useDebounce(emailSearch, 400);

  // Keep the search box in sync when filters.email changes from outside (e.g. a reset).
  useEffect(() => {
    setEmailSearch(filters.email || '');
  }, [filters.email]);

  useEffect(() => {
    if (debouncedEmail !== (filters.email || '')) {
      onChange({ email: debouncedEmail });
    }
    // Only re-run when the debounced value settles, not when `filters`/`onChange` identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmail]);

  return (
    <div className="filters">
      <div className="search">
        <span>⌕</span>
        <input
          type="text"
          placeholder="Search fan by email…"
          value={emailSearch}
          onChange={e => setEmailSearch(e.target.value)}
          style={{
            border: 'none', background: 'transparent', outline: 'none', flex: 1, font: 'inherit', color: 'inherit',
          }}
        />
      </div>

      <select
        className="drop"
        value={filters.type}
        onChange={e => onChange({ type: e.target.value })}
        aria-label="Filter by type"
      >
        {TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>

      <select
        className="drop"
        value={filters.slug}
        onChange={e => onChange({ slug: e.target.value })}
        aria-label="Filter by prize"
      >
        <option value="">Prize · Any</option>
        {slugOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>

      <select
        className="drop"
        value={filters.claimed}
        onChange={e => onChange({ claimed: e.target.value })}
        aria-label="Filter by claimed status"
      >
        {CLAIMED_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>

      <input
        type="date"
        className="drop"
        value={filters.startDate || ''}
        onChange={e => onChange({ startDate: e.target.value })}
        aria-label="Start date"
      />
      <input
        type="date"
        className="drop"
        value={filters.finishDate || ''}
        onChange={e => onChange({ finishDate: e.target.value })}
        aria-label="Finish date"
      />

      <span className="spacer" />
      <button type="button" className="ghost" onClick={onExport} disabled={downloadingInProgress}>
        {downloadingInProgress
          ? <Loader type="TailSpin" color="#000" height="20" width="20" />
          : 'Export CSV'}
      </button>
    </div>
  );
};

LoyaltyToolbar.propTypes = {
  filters: PropTypes.shape({
    email: PropTypes.string,
    type: PropTypes.string,
    slug: PropTypes.string,
    claimed: PropTypes.string,
    startDate: PropTypes.string,
    finishDate: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  slugOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })),
  onExport: PropTypes.func,
  downloadingInProgress: PropTypes.bool,
};

LoyaltyToolbar.defaultProps = {
  slugOptions: [],
  onExport: () => {},
  downloadingInProgress: false,
};

export default LoyaltyToolbar;
